import * as React from "react";
import axios from "axios";
import { Plus, RefreshCw, Trash2, TrendingDown, Wallet } from "lucide-react";

import { AppLayout } from "../../components/ui-v2/layout/AppLayout";
import { EmptyState } from "../../components/ui-v2/empty-state";
import { Card, CardContent } from "../../components/ui-v2/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  SortableTableHead,
} from "../../components/ui-v2/table";
import { Button, RowIconAction } from "../../components/ui-v2/button";
import { Badge } from "../../components/ui-v2/badge";
import { Skeleton } from "../../components/ui-v2/skeleton";
import { FilterPill, FilterPillGroup } from "../../components/ui-v2/filter-pill";
import { StatCard } from "../../components/ui-v2/stat-card";
import { CurrencyValue } from "../../components/ui-v2/value-display";
import { Heading, Text, MonoText } from "../../components/ui-v2/typography";
import { AddContributionDialog } from "../../components/ui-v2/AddContributionDialog";
import { DeleteContributionDialog } from "../../components/ui-v2/DeleteContributionDialog";
import { API_BASE_URL } from '../../lib/api';

/**
 * V2 Contributions Page — Institutional Console redesign (issue #236,
 * DESIGN-SPEC.md §5 "Contributions": "KPI strip -> history table with
 * Deposit/Withdrawal badges").
 *
 * Lives under the "Activity" top-level nav section; AppLayout/PageToolbar
 * renders the Feed/Realised gains/Dividends/Withdrawals/Contributions/
 * Events(N) sub-tabs from the route (navRegistry.js) — this component only
 * supplies the shared "Activity" toolbar title (matching
 * Contributions.dc.html:22, same as Transactions.dc.html/Events.dc.html) and
 * the two toolbar actions ("Record withdrawal" / "+ Record deposit",
 * Contributions.dc.html:31-32) via AppLayout's secondaryActions/primaryAction
 * slots.
 *
 * KPI strip: Net contributions (EUR) / Deposits (xN) / Withdrawals (xN) / Net
 * contributions (USD) — matches Contributions.dc.html:37-62 exactly (all four
 * values are already returned by the existing `/contributions` +
 * `/contributions/total` endpoints; no new API calls).
 *
 * All prior functionality retained: fetch contributions + EUR/USD totals,
 * refresh, add deposit/withdrawal (opens the same AddContributionDialog with
 * a defaultType), delete (DeleteContributionDialog), empty/error/loading
 * states. New: All/Deposits/Withdrawals FilterPillGroup (client-side, additive
 * — was not present pre-restyle) and Date column sorting (client-side,
 * additive) per the mockup's "Date ↓" sortable header
 * (Contributions.dc.html:77). See session file for the full feature audit.
 */

function formatDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Plain formatted-currency string for StatCard `value` slots (matches the
 * CashFlow.jsx precedent — StatCard's value slot already applies font-mono/
 * 22px/600 styling; passing a `CurrencyValue` node there would double-wrap
 * the same type scale, the "StatCard double-styling" issue flagged as a
 * non-blocking note in the #235 review).
 */
function formatCurrency(value, currency) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(value || 0);
}

const TYPE_FILTERS = [
  { value: "all", label: "All" },
  { value: "deposit", label: "Deposits" },
  { value: "withdrawal", label: "Withdrawals" },
];

/**
 * Type badge tint (Contributions.dc.html `dep`/`wit` style constants):
 * Deposit -> positive tint, Withdrawal -> warning tint. Neither uses
 * `destructive` — a withdrawal isn't an error, matching the mockup's amber
 * (not red) treatment.
 */
function TypeBadge({ type }) {
  const isWithdrawal = type === "withdrawal";
  return (
    <Badge variant={isWithdrawal ? "warning" : "success"} size="sm">
      {isWithdrawal ? "Withdrawal" : "Deposit"}
    </Badge>
  );
}

/**
 * Loading skeleton — KPI strip + toolbar + table shape, single-cycle Skeleton
 * primitive (§6/§7: no shimmer >1 cycle, motion-reduce disables it).
 */
function LoadingState() {
  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={`stat-skeleton-${i}`} className="py-3.5 px-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-2 h-6 w-28" />
            <Skeleton className="mt-2 h-3 w-16" />
          </Card>
        ))}
      </div>
      <Card>
        <CardContent className="p-0">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={`row-skeleton-${i}`} className="flex items-center gap-4 border-b border-hairline px-[18px] py-2">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="ml-auto h-3 w-24" />
              <Skeleton className="h-3 w-32" />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

export function Contributions() {
  const [contributions, setContributions] = React.useState([]);
  const [totals, setTotals] = React.useState({ EUR: 0, USD: 0 });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [typeFilter, setTypeFilter] = React.useState("all");
  const [sortDirection, setSortDirection] = React.useState("desc");

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedContribution, setSelectedContribution] = React.useState(null);
  const [defaultType, setDefaultType] = React.useState("deposit");

  const fetchContributions = React.useCallback(async () => {
    try {
      const [contributionsRes, totalEURRes, totalUSDRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/contributions`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/contributions/total?currency=EUR`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/contributions/total?currency=USD`, { withCredentials: true }),
      ]);

      setContributions(contributionsRes.data.contributions || []);
      setTotals({
        EUR: totalEURRes.data.total || 0,
        USD: totalUSDRes.data.total || 0,
      });
      setError(null);
    } catch (err) {
      console.error("Error fetching contributions:", err);
      setError(err.message);
    }
  }, []);

  React.useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      await fetchContributions();
      setLoading(false);
    };
    loadInitialData();
  }, [fetchContributions]);

  const handleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);
    await fetchContributions();
    setIsRefreshing(false);
  }, [fetchContributions]);

  const handleAddDeposit = () => {
    setDefaultType("deposit");
    setAddDialogOpen(true);
  };

  const handleAddWithdrawal = () => {
    setDefaultType("withdrawal");
    setAddDialogOpen(true);
  };

  const handleDeleteClick = (contribution) => {
    setSelectedContribution(contribution);
    setDeleteDialogOpen(true);
  };

  const handleAddSuccess = () => {
    fetchContributions();
  };

  const handleDeleteSuccess = () => {
    fetchContributions();
    setSelectedContribution(null);
  };

  const handleSortToggle = () => {
    setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  // Calculate summary stats
  const summary = React.useMemo(() => {
    const deposits = contributions.filter((c) => c.type === "deposit");
    const withdrawals = contributions.filter((c) => c.type === "withdrawal");

    const totalDeposits = deposits.reduce((sum, c) => sum + c.amount, 0);
    const totalWithdrawals = withdrawals.reduce((sum, c) => sum + c.amount, 0);

    return {
      totalDeposits,
      totalWithdrawals,
      depositCount: deposits.length,
      withdrawalCount: withdrawals.length,
      netContributions: totals.EUR,
    };
  }, [contributions, totals]);

  const visibleContributions = React.useMemo(() => {
    let rows = contributions;
    if (typeFilter !== "all") {
      rows = rows.filter((c) => c.type === typeFilter);
    }
    return [...rows].sort((a, b) => {
      const diff = new Date(a.date) - new Date(b.date);
      return sortDirection === "desc" ? -diff : diff;
    });
  }, [contributions, typeFilter, sortDirection]);

  const primaryAction = (
    <Button size="sm" onClick={handleAddDeposit}>
      <Plus className="h-3.5 w-3.5 mr-1.5" />
      Record deposit
    </Button>
  );

  const secondaryActions = (
    <Button variant="outline" size="sm" onClick={handleAddWithdrawal}>
      <TrendingDown className="h-3.5 w-3.5 mr-1.5" />
      Record withdrawal
    </Button>
  );

  // Loading state
  if (loading) {
    return (
      <AppLayout title="Activity" primaryAction={primaryAction} secondaryActions={secondaryActions}>
        <LoadingState />
      </AppLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <AppLayout title="Activity" primaryAction={primaryAction} secondaryActions={secondaryActions}>
        <Card>
          <CardContent className="py-16">
            <div className="text-center">
              <Heading level={4} className="text-destructive">
                Failed to load contributions
              </Heading>
              <Text variant="secondary" className="mt-2">
                {error}
              </Text>
              <Button variant="outline" className="mt-4" onClick={handleRefresh}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </AppLayout>
    );
  }

  // Empty state
  if (contributions.length === 0) {
    return (
      <AppLayout title="Activity" primaryAction={primaryAction} secondaryActions={secondaryActions}>
        <Card>
          <EmptyState
            icon={Wallet}
            title="No Contributions Yet"
            description="Start tracking your portfolio contributions by recording deposits when you add money and withdrawals when you take money out. This helps calculate your true investment performance."
            primaryAction={{
              label: "Record Deposit",
              onClick: handleAddDeposit,
            }}
            secondaryAction={{
              label: "Record Withdrawal",
              onClick: handleAddWithdrawal,
            }}
          />
        </Card>

        <AddContributionDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          onSuccess={handleAddSuccess}
          defaultType={defaultType}
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Activity" primaryAction={primaryAction} secondaryActions={secondaryActions}>
      <div className="mx-auto flex max-w-[1240px] flex-col gap-3.5">
        {/* KPI strip (Contributions.dc.html:37-62) */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard
            label="Net contributions (EUR)"
            value={formatCurrency(summary.netContributions, "EUR")}
            valueTone={summary.netContributions >= 0 ? "positive" : "negative"}
            delta="total invested"
          />
          <StatCard
            label="Deposits"
            value={formatCurrency(summary.totalDeposits, "EUR")}
            delta={`×${summary.depositCount}`}
          />
          <StatCard
            label="Withdrawals"
            value={formatCurrency(summary.totalWithdrawals, "EUR")}
            delta={`×${summary.withdrawalCount}`}
          />
          <StatCard
            label="Net contributions (USD)"
            value={formatCurrency(totals.USD, "USD")}
            delta="USD accounts"
          />
        </div>

        {/* Transaction history table */}
        <Card className="overflow-hidden p-0">
          <div className="flex items-center gap-3 border-b border-hairline px-[18px] py-2.5">
            <Heading level={4} className="text-[13.5px] font-semibold">
              Transaction history
            </Heading>
            <MonoText size="xs" className="text-text-faint">
              {visibleContributions.length} transactions
            </MonoText>
            <div className="ml-auto flex items-center gap-2">
              <FilterPillGroup>
                {TYPE_FILTERS.map((filter) => (
                  <FilterPill
                    key={filter.value}
                    active={typeFilter === filter.value}
                    onClick={() => setTypeFilter(filter.value)}
                  >
                    {filter.label}
                  </FilterPill>
                ))}
              </FilterPillGroup>
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshCw className={`h-3.5 w-3.5 sm:mr-1.5 ${isRefreshing ? "animate-spin motion-reduce:animate-none" : ""}`} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  className="w-[120px] px-[18px]"
                  sortDirection={sortDirection}
                  onSort={handleSortToggle}
                >
                  Date
                </SortableTableHead>
                <TableHead className="w-[140px]">Type</TableHead>
                <TableHead className="w-[140px] text-right">Amount</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[60px] px-[18px] text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleContributions.map((contribution) => {
                const isWithdrawal = contribution.type === "withdrawal";

                return (
                  <TableRow key={contribution.contributionId}>
                    <TableCell className="px-[18px] font-mono text-[11.5px] text-muted-token">
                      {formatDate(contribution.date)}
                    </TableCell>
                    <TableCell>
                      <TypeBadge type={contribution.type} />
                    </TableCell>
                    <TableCell className="text-right">
                      <CurrencyValue
                        value={isWithdrawal ? -contribution.amount : contribution.amount}
                        currency={contribution.currency}
                        size="sm"
                        colorize
                        showSign
                        className="font-semibold"
                      />
                    </TableCell>
                    <TableCell className="text-xs text-muted-token">
                      <span className="block max-w-[280px] truncate" title={contribution.description}>
                        {contribution.description || "-"}
                      </span>
                    </TableCell>
                    <TableCell className="px-[18px] text-right">
                      <RowIconAction
                        intent="negative"
                        label={`Delete ${contribution.type}`}
                        onClick={() => handleDeleteClick(contribution)}
                      >
                        <Trash2 />
                      </RowIconAction>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Dialogs */}
      <AddContributionDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSuccess={handleAddSuccess}
        defaultType={defaultType}
      />

      <DeleteContributionDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSuccess={handleDeleteSuccess}
        contribution={selectedContribution}
      />
    </AppLayout>
  );
}

export default Contributions;
